import type {
  AdminCapability,
  AdminRole,
  AdminSection,
  AdminSession,
  AdminUser,
} from '../../types'

type SecurityTranslate = (key: string, params?: Record<string, string | number>) => string

const coerceStringArray = (input: unknown): string[] =>
  Array.isArray(input) ? input.map((value) => String(value)).filter(Boolean) : []

export const toSession = (t: SecurityTranslate, payload: unknown): AdminSession => {
  const raw = (payload ?? {}) as Record<string, unknown>
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : typeof raw._id === 'string'
      ? raw._id
      : ''

  return {
    id,
    ip:
      typeof raw.ip === 'string'
        ? raw.ip
        : typeof raw.location === 'object' && raw.location
        ? ((raw.location as Record<string, unknown>).ip as string | null | undefined) ?? null
        : null,
    deviceName:
      typeof raw.deviceName === 'string' ? raw.deviceName : t('security.sessions.unknownDevice'),
    userAgent: typeof raw.userAgent === 'string' ? raw.userAgent : null,
    location:
      raw.location && typeof raw.location === 'object'
        ? (raw.location as AdminSession['location'])
        : null,
    trusted: Boolean(raw.trusted),
    trustedAt: typeof raw.trustedAt === 'string' ? raw.trustedAt : null,
    revokedAt: typeof raw.revokedAt === 'string' ? raw.revokedAt : null,
    createdAt:
      typeof raw.createdAt === 'string'
        ? raw.createdAt
        : new Date().toISOString(),
    lastSeenAt:
      typeof raw.lastSeenAt === 'string'
        ? raw.lastSeenAt
        : typeof raw.createdAt === 'string'
        ? raw.createdAt
        : new Date().toISOString(),
    isCurrent: Boolean(raw.isCurrent),
  }
}

export const toUser = (t: SecurityTranslate, payload: unknown): AdminUser => {
  const raw = (payload ?? {}) as Record<string, unknown>
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : typeof raw._id === 'string'
      ? raw._id
      : ''

  return {
    id,
    username: typeof raw.username === 'string' ? raw.username : t('security.team.userDefault'),
    displayName:
      typeof raw.displayName === 'string'
        ? raw.displayName
        : typeof raw.username === 'string'
        ? raw.username
        : t('security.team.displayFallback'),
    email: typeof raw.email === 'string' ? raw.email : '',
    roles: coerceStringArray(raw.roles),
    lastLoginAt: typeof raw.lastLoginAt === 'string' ? raw.lastLoginAt : null,
    lastLoginIp: typeof raw.lastLoginIp === 'string' ? raw.lastLoginIp : null,
    lastLoginLocation:
      raw.lastLoginLocation && typeof raw.lastLoginLocation === 'object'
        ? (raw.lastLoginLocation as AdminUser['lastLoginLocation'])
        : null,
    avatarUrl: typeof raw.avatarUrl === 'string' ? raw.avatarUrl : undefined,
  }
}

export const toRole = (payload: unknown): AdminRole => {
  const raw = (payload ?? {}) as Record<string, unknown>
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : typeof raw._id === 'string'
      ? raw._id
      : ''

  return {
    id,
    name: typeof raw.name === 'string' ? raw.name : id,
    label:
      typeof raw.label === 'string'
        ? raw.label
        : typeof raw.name === 'string'
        ? raw.name
        : id,
    description: typeof raw.description === 'string' ? raw.description : '',
    permissions: coerceStringArray(raw.permissions),
    capabilities: coerceStringArray(raw.capabilities),
  }
}

export const toCapability = (payload: unknown): AdminCapability => {
  const raw = (payload ?? {}) as Record<string, unknown>
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : typeof raw._id === 'string'
      ? raw._id
      : ''

  return {
    id,
    key: typeof raw.key === 'string' ? raw.key : id,
    label: typeof raw.label === 'string' ? raw.label : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    category: typeof raw.category === 'string' ? raw.category : 'Custom',
    order: typeof raw.order === 'number' ? raw.order : 999,
    isSystem: Boolean(raw.isSystem),
  }
}

export const toSection = (payload: unknown): AdminSection => {
  const raw = (payload ?? {}) as Record<string, unknown>
  const id =
    typeof raw.id === 'string'
      ? raw.id
      : typeof raw._id === 'string'
      ? raw._id
      : ''

  return {
    id,
    key: typeof raw.key === 'string' ? raw.key : id,
    label: typeof raw.label === 'string' ? raw.label : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    requiredCapabilities: coerceStringArray(raw.requiredCapabilities),
    order: typeof raw.order === 'number' ? raw.order : 999,
    isSystem: Boolean(raw.isSystem),
  }
}

export const sortSessions = (items: AdminSession[]) =>
  [...items].sort(
    (a, b) =>
      new Date(b.lastSeenAt ?? b.createdAt).getTime() -
      new Date(a.lastSeenAt ?? a.createdAt).getTime(),
  )
